import { useTranslations } from 'next-intl';

import type { OddsPageLayout } from '@/types';
import type { SportMatchModel } from '@nf/types/odds';
import { BeachVolleyballLiveScore, LiveScoreUnion } from '@nf/types/scoreboard';
import { LiveTimer } from '@nf/types/live-timer';
import { Breakpoint } from '@/features/breakpoint';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { MatchOption } from '@/features/odds-page/components/odds-page-items/match-option';

import { RainIcon, CoffeeIcon, ArrowRightIcon } from '@/components/icons/svg-icons';
import { MyFavouritesMatchButton } from '@/features/my-favourites/components/my-favourites-match-button';

type SportMatchModelWithLiveScore = SportMatchModel & {
	ls?: LiveScoreUnion;
}

interface MatchEventCardProps {
	match: SportMatchModelWithLiveScore;
	liveTimer: LiveTimer;
	betTypeCount: number;
	oddsPageLayout: OddsPageLayout;
}

export const MatchEventCard = ({
	match,
	liveTimer,
	betTypeCount,
	oddsPageLayout
}: MatchEventCardProps) => {
	const t = useTranslations('General');
	const { breakpoint } = useBreakpoint();

	const { IsLive, GameID, MatchId, Ktm, Etm } = match;
	const isSlideContainer = liveTimer.event && (liveTimer.matchStatus || liveTimer.countdownDomRef);
	const isShowMatchStatus = liveTimer.matchStatus && !liveTimer.countdownDomRef;
	const otherMatchStatus = liveTimer.checkOtherMatchStatus;
	const isListLayout = oddsPageLayout === 'list';

	const shouldShowMatchOption = isListLayout && 
		(!liveTimer.event || (liveTimer.event && breakpoint > Breakpoint.md));

	return (
		<div className="match-info">
			{isListLayout && (
				<MyFavouritesMatchButton 
					matchId={MatchId} 
					time={Ktm || Etm} 
					sportType={GameID} 
				/>
			)}

			{!IsLive 
				? (
					<div 
						className="match-timer" 
						dangerouslySetInnerHTML={{ __html: liveTimer.matchBeginTime }} 
					/>
				) : (
					<div className="match-timer">
						{isShowMatchStatus && !isSlideContainer ? (
							<div className="match-time-live">
								{liveTimer.matchStatus === 'Ref_live' ? t('lbl_live') : liveTimer.matchStatus}
							</div>
						) : null}

						{isSlideContainer 
							? <MatchEventStatusInfo liveTimer={liveTimer} /> 
							: (
								<div 
									ref={liveTimer.countdownDomRef} 
									className="match-time-live" 
								/>
							)
						}

						{otherMatchStatus || (GameID === 45 && (match.ls as BeachVolleyballLiveScore)?.rain === '1') ? (
							<>
								{otherMatchStatus === 'Rain' || 
									(GameID === 45 && (match.ls as BeachVolleyballLiveScore)?.rain === '1') 
									? <RainIcon /> 
									: null
								}
								{otherMatchStatus === 'Coffee' ? <CoffeeIcon /> : null}
							</>
						) : null}
					</div>
				)
			}
			
			{shouldShowMatchOption ? (
				<MatchOption 
					match={match} 
					oddsPageLayout={oddsPageLayout}
				/>
			) : null}
			
			{isListLayout ? (
				<div className="btn-more-match">
					<span>{`${betTypeCount < 100 ? betTypeCount : '99+'}`}</span>
					<ArrowRightIcon />
				</div>
			) : null}
		</div>
	);
};

const MatchEventStatusInfo = ({ 
	liveTimer 
}: { 
	liveTimer: LiveTimer; 
}) => {
	const t = useTranslations('General');
	
	return (
		<div className="match-slide">
			<div className="match-slide__item">
				{liveTimer.countdownDomRef ? (
					<div 
						ref={liveTimer.countdownDomRef}
						className="match-time-live"  
					/>
				) : (
					<div className="match-time-live">
						{liveTimer.matchStatus}
					</div>
				)}
			</div>
			<div className="match-slide__item">
				<div className="match-status-tag">{t(liveTimer.event)}</div>
			</div>
		</div>
	);
};